import React, { useRef } from 'react'
import PropTypes from 'prop-types'
import dynamic from 'next/dynamic'
import styles from './style.module.scss'

import CommentaryCommonBox from '../commentaryCommonBox'
import CustomLink from '@shared/components/customLink'
import { getMatchPlayers } from '@shared/libs/match-detail'
import useWindowSize from '@shared/hooks/windowSize'
import SeparatorCommon from '@shared/components/separatorCommon'
import EndOverRC from './CommentaryRC/endOverRC'
import EndOver from './endover'
import { reviveAdData } from '@shared/components/reviveAds/reviveAdData'

const ReviveAds = dynamic(() => import('@shared/components/reviveAds'), { ssr: false })

const Commentary = ({ data = [], id, inning, is100, activeTab }) => {
  const [width] = useWindowSize()
  const players = getMatchPlayers()

  const previousId = useRef()
  const countCommentary = data?.length - 1
  let overEnd = -1
  let banInd = -1
  const { addLeft } = reviveAdData

  const renderScore = over => {
    const score = over?.sScore
    if (score === '0') return <div className={`${styles.run} rounded-pill`}>{score}</div>
    if (['4', '4nb'].includes(score)) { return <div className={`${styles.run} rounded-pill bg-success border-success text-white`}>{score.toUpperCase()}</div> }
    if (['6', '6nb'].includes(score)) return <div className={`${styles.run} rounded-pill bg-success text-white`}>{score.toUpperCase()}</div>
    if (['w'].includes(score)) return <div className={`${styles.run} rounded-pill redSix text-white`}>{score.toUpperCase()}</div>
    if (['4b', '4lb'].includes(score)) return <div className={`${styles.run} rounded-pill`}>{score}</div>
    if ((over?.nRuns > 0 && over?.nRuns < 4) || over?.nRuns === 5 || over?.nRuns > 6) { return <div className={`${styles.run} rounded-pill`}>{score}</div> }
    return null
  }

  const renderCommentaryItem = (over, index, item) => {
    const previous = item[index - 1]
    const oWicketBatter = players[over?.oWicketBatter?._id] || over?.oWicketBatter
    previousId.current = previous

    if (over?.eEvent === 'w' && index === 0 && activeTab === 'all') return null

    if (
      over?.sEventId !== previous?.sEventId &&
      over?.sEventId !== '0-1' &&
      over?.sEventId !== '0' &&
      !['bai', 'bao', 'bam', 'boi', 'tm'].includes(over?.eEvent)
    ) {
      const isEndEvent = ['oe', 'ie'].includes(over?.eEvent)
      if (isEndEvent) {
        ++overEnd
        if (overEnd % 2 === 0) {
          ++banInd
          if ((width < 768 && banInd > 5) || (width >= 768 && banInd > 2)) {
            banInd = 0
          }
        }
      }

      const isFirstBallOrLast =
        (over?.sBall === '1' || countCommentary === index) &&
        (!item[index + 1]?.eEvent || ['oe', 'ie', 'to', 'p11'].includes(item[index + 1]?.eEvent))

      const classNames = [
        'content-visibility-auto',
        styles.item,
        index,
        isEndEvent && styles.endItem,
        isFirstBallOrLast && styles.firstItem,
        over?.nInningNumber === 1 && styles.nInning1,
        over?.nInningNumber === 2 && styles.nInning2,
        over?.nInningNumber === 3 && styles.nInning3,
        over?.nInningNumber === 4 && styles.nInning4
      ]
        .filter(Boolean)
        .join(' ')

      return (
        <div key={`${index}${over?.sEventId}`} id={`${over?.sEventId}${over?.sOver}${over?.nInningNumber}`} className={classNames}>
          {previous && previous?.nInningNumber !== over?.nInningNumber && (
            <SeparatorCommon separatorWord={`${'End of inning'} ${over?.nInningNumber}`} />
          )}

          {!isEndEvent ? (
            <div
              className={`${over?.sComSummary ? styles.alignItems : styles.alignItemsCenter} ${styles.itemInner} d-flex ${
                (!previous || previous?.nInningNumber !== over?.nInningNumber) && styles.firstItem
              }`}
              id={over?.sEventId}>
              <div className={`${styles.ball} d-flex flex-column flex-md-row-reverse align-items-center text-center`}>
                {renderScore(over)}
                <span className={`${styles.ballOver} font-bold`}>{is100 ? `${over?.sOver}` : `${over?.sOver}.${over?.sBall}`}</span>
              </div>
              <div>
                <p className="mb-0 text-wrap">
                  {over?.sComSummary ? `${over?.sComSummary} ` : ''}
                  {over?.eEvent === 'w' && oWicketBatter && (
                    <b>
                      {oWicketBatter?.eTagStatus === 'a' && oWicketBatter?.oSeo?.eStatus === 'a' ? (
                        <CustomLink href={`/${oWicketBatter?.oSeo?.sSlug}`}>
                          {oWicketBatter?.sFullName || oWicketBatter?.sShortName}
                        </CustomLink>
                      ) : (
                        oWicketBatter?.sFullName || oWicketBatter?.sShortName
                      )}
                      {`${over?.nBatterRuns}(${over?.nBatterBalls})`}
                    </b>
                  )}
                  <b>{over?.sHowOut}</b>
                </p>
                {over?.sText && <p className="mb-0 mt-1 text-wrap">{over?.sText}</p>}
              </div>
            </div>
          ) : (
            <>
              {overEnd % 2 === 1 && <ReviveAds {...addLeft} className="d-flex justify-content-center mt-4" />}
              {!is100 &&
                (width < 768 ? (
                  <EndOver data={over} id={over?.sEventId} inning={inning} />
                ) : (
                  <EndOverRC data={over} id={over?.sEventId} inning={inning} />
                ))}
            </>
          )}
        </div>
      )
    }

    return null
  }

  const renderTossDetail = over => (
    <CommentaryCommonBox className="mt-3 content-visibility-auto" title="Toss Detail">
      {over?.sComSummary || over?.sCommentary}
    </CommentaryCommonBox>
  )

  const renderPlaying11 = over => (
    <>
      {over?.oTeamA?.sPlayers && (
        <CommentaryCommonBox className="mt-3 content-visibility-auto" title={`${over?.oTeamA?.sTeam} ${'Playing XI'}`}>
          {over?.oTeamA?.sPlayers}
        </CommentaryCommonBox>
      )}
      {over?.oTeamB?.sPlayers && (
        <CommentaryCommonBox className="content-visibility-auto" title={`${over?.oTeamB?.sTeam} ${'Playing XI'}`}>
          {over?.oTeamB?.sPlayers}
        </CommentaryCommonBox>
      )}
    </>
  )

  return (
    <section className={`${styles.commentary} overflow-hidden mb-3`}>
      {data?.map((over, index, item) => {
        if (over?.sEventId === '0' && over?.sComSummary) {
          return <React.Fragment key={`comm-${index}`}>{renderTossDetail(over)}</React.Fragment>
        }

        if (over?.sEventId === '0-1') {
          return <React.Fragment key={`play11-${index}`}>{renderPlaying11(over)}</React.Fragment>
        }

        if (over?.eEvent === 'to') {
          return <React.Fragment key={`toss-${index}`}>{renderTossDetail(over)}</React.Fragment>
        }

        const rendered = renderCommentaryItem(over, index, item)
        if (rendered) return rendered

        return (
          <div key={`${over?.sEventId}${over?.sOver}${over?.nInningNumber}`} id={`${over?.sEventId}${over?.sOver}${over?.nInningNumber}`} />
        )
      })}
    </section>
  )
}

Commentary.propTypes = {
  data: PropTypes.array,
  id: PropTypes.string,
  inning: PropTypes.array,
  activeTab: PropTypes.string,
  is100: PropTypes.bool
}

export default Commentary
